import { api } from "./client";
import type { Tier } from "../components/TierBadge";

export type { Tier };

export interface Beneficiary {
  name: string;
  phone: string;
  relation: string;
  line_user_id?: string | null;
  added_at?: string;
}

export interface Address {
  city?: string;
  district?: string;
  detail?: string;
}

// 推薦來源(入會時填的推薦碼解析結果,業績分潤歸因用)
export interface ReferredBy {
  code: string;
  type?: string;
  label?: string;
  owner_uid?: string;
  resolved?: boolean;
  at?: string;
}

// 會員端 /api/members/me(含 /me/join、/me/profile)只回白名單欄位 — 後端 member_service.PUBLIC_MEMBER_FIELDS。
// tags / referred_by / is_following / suspended_by_admin / last_interaction_at / picture_url 只有 /api/admin 會回
// (後台頁共用這個 type)。會員端頁面要讀新欄位,後端白名單要一起加,不然讀到的永遠是 undefined。
export interface Member {
  line_user_id: string;
  name?: string;
  display_name?: string;
  phone?: string;
  phone_verified?: boolean;
  email?: string;
  birthday?: string;
  address?: Address;
  tier?: Tier;
  status?: "pending" | "active" | "suspended";
  /** 有沒有加官方帳號好友。封鎖 / 移除好友 → false(與 status 分開,2026-09-16) */
  is_following?: boolean;
  /** true = 這個 suspended 是後台人工停用的,不是客戶封鎖造成的 */
  suspended_by_admin?: boolean;
  unfollowed_at?: string;
  membership_start?: string;
  membership_end?: string;
  beneficiaries?: Beneficiary[];
  tags?: string[];
  total_spent?: number;
  total_orders?: number;
  last_interaction_at?: string;
  picture_url?: string;
  referred_by?: ReferredBy;
  /** #16 入會偏好問卷作答(2026-10-06,白名單有放行;首頁靠 completed_at 決定要不要顯示問卷卡) */
  survey?: { answers?: Record<string, string>; completed_at?: string };
}

export async function liffVerify(payload: {
  accessToken: string;
  profile: { userId: string; displayName: string; pictureUrl?: string };
}): Promise<{ jwt: string; member: any }> {
  const { data } = await api.post("/api/auth/liff-verify", payload);
  return data;
}

export async function getMe(): Promise<Member> {
  const { data } = await api.get("/api/members/me");
  return data.member;
}

// 會員資料頁存檔:後端只收這三欄(member_service._BASIC_INFO_FIELDS),其餘欄位一律忽略。
// 手機不在裡面 —— 只能經 completeProfile(POST /me/profile)首次填寫,之後要改請專屬顧問從後台改。
// 留過手機 + 有姓名後姓名也鎖(後端忽略),MemberProfile 那時就不送 name。
export async function updateMe(fields: Pick<Member, "name" | "email" | "birthday">) {
  const { data } = await api.put("/api/members/me", fields);
  return data;
}

// 一鍵加入(2026-09-24 取代入會表單 + 驗證碼):同意條款即成為會員,不收姓名 / 手機
export async function joinMember(form: {
  consent: boolean;
  referral_code?: string;
}): Promise<{ status: string; member?: Member }> {
  const { data } = await api.post("/api/members/me/join", form);
  return data;
}

// 補齊姓名 / 手機(+ 選填 email / 生日 / 地址)— 結帳、送禮前才需要
export async function completeProfile(form: {
  name: string;
  phone: string;
  email?: string;
  birthday?: string;
  address_city?: string;
  address_district?: string;
  address_detail?: string;
}): Promise<{ status: string; member?: Member; already_complete?: boolean; missing?: string[] }> {
  const { data } = await api.post("/api/members/me/profile", form);
  return data;
}

// 首頁「三題偏好問卷」卡 — 把下一題問卷卡 push 到會員的 LINE 聊天室(答題在聊天室用按鈕完成,不開 LIFF)
// status: pushed 已送出 / completed 三題都答完了 / disabled 問卷停用 / member_not_found 查無會員(後端回 404 會 throw)
export async function pushJoinSurvey(): Promise<{ status: string }> {
  const { data } = await api.post("/api/members/me/survey/push");
  return data;
}

// 加入頁即時驗證推薦碼 — 回是否有效 + 推薦人名稱
export async function checkReferralCode(
  code: string,
): Promise<{ valid: boolean; label: string; type: string }> {
  const { data } = await api.get("/api/members/me/referral-check", { params: { code } });
  return data;
}

export async function addBeneficiary(b: Omit<Beneficiary, "added_at">) {
  const { data } = await api.post("/api/members/me/beneficiaries", b);
  return data;
}

export async function removeBeneficiary(phone: string) {
  const { data } = await api.delete("/api/members/me/beneficiaries", {
    data: { phone },
  });
  return data;
}

// 取自己的推薦碼(沒有後端會自動產一組)
export async function getMyReferralCode(): Promise<string> {
  const { data } = await api.get("/api/members/me/referral-code");
  return data.code as string;
}
